/** Immutable undo/redo stack for plain values. */
export interface History<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];
}

export const MAX_HISTORY = 60;

export const createHistory = <T>(present: T): History<T> => ({ past: [], present, future: [] });

/** Records a new state. Identical values are ignored so no-op edits don't clutter undo. */
export function pushHistory<T>(history: History<T>, next: T): History<T> {
  if (Object.is(next, history.present)) return history;
  return { past: [...history.past, history.present].slice(-MAX_HISTORY), present: next, future: [] };
}

/** Replaces the present without creating an undo step (e.g. while dragging). */
export const replacePresent = <T>(history: History<T>, next: T): History<T> => ({ ...history, present: next });

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past.at(-1);
  if (previous === undefined) return history;
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future] };
}

export function redo<T>(history: History<T>): History<T> {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return { past: [...history.past, history.present], present: next, future: rest };
}

/**
 * Finishes a continuous gesture (drag, slider) that updated the present with
 * {@link replacePresent}: records `base` — the value before the gesture — as one
 * undo step. Nothing is recorded if the gesture ended where it started.
 */
export function commitFrom<T>(history: History<T>, base: T): History<T> {
  if (Object.is(history.present, base)) return history;
  return { past: [...history.past, base].slice(-MAX_HISTORY), present: history.present, future: [] };
}
